// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

const CHUNK_LOAD_RECOVERY_KEY = 'frontend.chunk-load-recovery.attempted-at';
const CHUNK_LOAD_RECOVERY_WINDOW_MS = 60_000;

type RecoveryOptions = {
  location?: Pick<Location, 'reload'>;
  storage?: Storage;
};

type RegisterRecoveryOptions = RecoveryOptions & {
  windowObject?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
};

function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const maybeMessage = 'message' in error ? error.message : undefined;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return '';
}

export function isDynamicChunkLoadError(error: unknown): boolean {
  const message = extractErrorMessage(error);
  return [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'Loading chunk',
    'Unable to preload CSS',
  ].some((fragment) => message.includes(fragment));
}

function shouldReloadForChunkFailure(storage: Storage, now = Date.now()): boolean {
  const lastAttempt = Number(storage.getItem(CHUNK_LOAD_RECOVERY_KEY) || 0);
  if (
    Number.isFinite(lastAttempt) &&
    lastAttempt > 0 &&
    now - lastAttempt < CHUNK_LOAD_RECOVERY_WINDOW_MS
  ) {
    return false;
  }
  storage.setItem(CHUNK_LOAD_RECOVERY_KEY, String(now));
  return true;
}

export function attemptChunkLoadRecovery(error: unknown, options: RecoveryOptions = {}): boolean {
  const location = options.location ?? window.location;
  const storage = options.storage ?? window.sessionStorage;
  if (!isDynamicChunkLoadError(error)) return false;
  if (!shouldReloadForChunkFailure(storage)) return false;
  location.reload();
  return true;
}

export function registerChunkLoadRecovery(options: RegisterRecoveryOptions = {}): () => void {
  const windowObject = options.windowObject ?? window;
  const handler = (event: Event) => {
    const detail = event instanceof CustomEvent ? event.detail : undefined;
    if (attemptChunkLoadRecovery(detail, options)) {
      event.preventDefault();
    }
  };

  windowObject.addEventListener('vite:preloadError', handler);
  return () => {
    windowObject.removeEventListener('vite:preloadError', handler);
  };
}
